import {
    Sofa,
    Building2,
    DraftingCompass,
    Hammer,
    LineChart,
} from "lucide-react";

export const SERVICES = [
    {
        slug: "interior-design",
        n: "01",
        title: "Interior Design",
        icon: Sofa,
        span: "md:col-span-4",
        image: "/images/projects/kolam-house.jpg",
        desc: "Private worlds composed in teak, cane and calibrated light — rooms that feel inevitable.",
        tagline: "Rooms that feel inevitable.",
        overview:
            "From the first mood film to the final cushion, our interiors are composed as complete worlds. We work in Kerala's material language — carved teak, rattan cane, lime plaster and brass — detailed to an international standard and tuned to the way tropical light actually moves through a day.",
        process: [
            { title: "Concept & Mood", desc: "References, rituals and light studies distilled into one clear idea." },
            { title: "Material Palette", desc: "Teak, cane, stone and textile sampled against real site light." },
            { title: "Joinery & Craft", desc: "Custom furniture drawn by us, built by Kochi's finest hands." },
            { title: "Styling & Handover", desc: "Every object placed, every lamp dimmed, before you walk in." },
        ],
        materials: ["Carved Teak", "Rattan Cane", "Lime Plaster", "Brass", "Terracotta", "Handloom Textile"],
        projects: ["The Kolam House", "The Courtyard House"],
    },
    {
        slug: "construction",
        n: "02",
        title: "Construction",
        icon: Building2,
        span: "md:col-span-2",
        image: "/images/projects/monsoon-villa.jpg",
        desc: "Engineering precision from foundation to final finish, delivered by our own site teams.",
        tagline: "Built for a hundred monsoons.",
        overview:
            "Our own site teams build what we draw — no handoffs, no lost intent. Structures are engineered for Kerala's rains and humidity: deep overhangs, honest concrete, clay-tiled slopes and drainage detailed like jewellery. Tolerances are measured in millimetres, not compromise.",
        process: [
            { title: "Site & Soil", desc: "Topography, water table and wind mapped before a line is poured." },
            { title: "Structure", desc: "RCC frames and laterite walls raised with surgical tolerance." },
            { title: "Envelope", desc: "Roofs, skins and screens sealed against the monsoon." },
            { title: "Finishing", desc: "Plaster, polish and punch-lists until nothing is left to note." },
        ],
        materials: ["Laterite", "RCC", "Clay Roof Tile", "Teak", "Granite", "Lime Mortar"],
        projects: ["Monsoon Villa", "The Courtyard House"],
    },
    {
        slug: "planning",
        n: "03",
        title: "Planning",
        icon: DraftingCompass,
        span: "md:col-span-2",
        image:
            "https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwY29uc3RydWN0aW9ufGVufDB8fHx8MTc4NTUwMDE1NXww&ixlib=rb-4.1.0&q=85",
        desc: "Feasibility, permits and master-planning navigated without friction.",
        tagline: "Every great building starts on paper.",
        overview:
            "We take sites from sketch to sanction without drama. Feasibility studies, massing models, KMBR permits and cost plans are sequenced so construction never waits on paperwork — and you never sign for a surprise.",
        process: [
            { title: "Feasibility", desc: "Zoning, FSI and budget reality-checked in the first week." },
            { title: "Concept Massing", desc: "Volumes tested against sun, rain, view and neighbour." },
            { title: "Permits", desc: "KMBR submissions and liaison handled end-to-end." },
            { title: "Cost Plan", desc: "A BOQ you can hold us to, line by line." },
        ],
        materials: ["Blueprints", "3D Massing", "BOQ & Estimates", "Permit Sets", "Site Analysis"],
        projects: ["Rthanya Dance Studio", "Bricklane Café"],
    },
    {
        slug: "renovation",
        n: "04",
        title: "Renovation",
        icon: Hammer,
        span: "md:col-span-2",
        image: "/images/kerala-courtyard.jpg",
        desc: "Heritage bones, contemporary soul. We rebuild history for the next century.",
        tagline: "Heritage bones, contemporary soul.",
        overview:
            "Old Kerala homes carry craft no new build can buy. We survey, stabilise and restore their teak bones, then insert contemporary comfort — courtyards, services and light — so the house serves another hundred years without losing its memory.",
        process: [
            { title: "Survey", desc: "Every beam, joint and crack documented before we touch a thing." },
            { title: "Stabilise", desc: "Structure repaired with lime, not shortcuts." },
            { title: "Restore", desc: "Carved teak and athangudi tile revived by specialist hands." },
            { title: "Insert", desc: "Modern kitchens, baths and services woven in invisibly." },
        ],
        materials: ["Reclaimed Teak", "Lime Mortar", "Athangudi Tile", "Laterite", "Brass Fittings"],
        projects: ["The Courtyard House", "The Kolam House"],
    },
    {
        slug: "consulting",
        n: "05",
        title: "Consulting",
        icon: LineChart,
        span: "md:col-span-2",
        image: "/images/projects/rthanya-studio.jpg",
        desc: "Senior counsel for developers, brands and private clients at acquisition stage.",
        tagline: "Senior counsel before you commit.",
        overview:
            "Before you buy the site or sign the contractor, sit with us. We advise developers, hospitality brands and private clients on acquisition, design audits and delivery oversight — the decisions that decide whether a project sings or sinks.",
        process: [
            { title: "Acquisition Review", desc: "Walk the site with us before you sign for it." },
            { title: "Due Diligence", desc: "Titles, sanctions and soil — verified, not assumed." },
            { title: "Design Audit", desc: "Your drawings stress-tested by people who build." },
            { title: "Delivery Oversight", desc: "Monthly site eyes that report to you, not the contractor." },
        ],
        materials: ["Site Reports", "Design Audits", "Cost Benchmarks", "Vendor Networks"],
        projects: ["Bricklane Café", "Rthanya Dance Studio"],
    },
];
